import { observable, action, makeObservable, flow } from 'decorators';
import Form, { isRequired } from 'components/form';
import { ValidationError } from 'errors';
import moment from 'moment';
import api from 'services/api';
import log from 'services/log';
import toastr from 'services/toastr';
import dialog, { DialogClosedError } from 'services/dialog';
import { toFileSize } from 'utils/number';
import { getFileExtension, getFileName } from 'utils/string';
import { isVideoType } from 'utils/dom';

const loadTus = () => import('tus-js-client');

// This chunk size given in examples
const CLOUDFLARE_STREAM_CHUNK_SIZE = 150 * 1024 * 1024;

export function getUrlPath(baseUrl, path) {
  const parts = baseUrl.split('?');
  return `${parts[0]}/${path}` + (parts.length === 1 ? '' : `?${parts[1]}`);
}

export default class DropzoneStore {
  @observable.shallow droppedFiles = [];

  @observable isUploading;

  constructor({ baseUrl, onUploaded, onUploading, onAllUploaded, onError, onManualFilesHandle, filters, createVideoTusUrl }) {
    makeObservable(this);
    this._baseUrl = baseUrl || 'files';
    this._onUploaded = onUploaded;
    this._onUploading = onUploading;
    this._onAllUploaded = onAllUploaded;
    this._createVideoTusUrl = createVideoTusUrl;
    this._onError = onError;
    this._onManualFilesHandle = onManualFilesHandle;
    this._filters = filters;
    this.form = new Form({
      fields: [
        {
          name: 'category',
          label: 'Category',
          type: 'selectasync',
          extra: {
            allowFreeInput: true,
            loadOptions: t => this._loadCategories(t)
          }
        }
      ]
    });
  }

  @action onDrop(files, fileRejections, autoUpload) {
    files.forEach(f => {
      this.droppedFiles.push(new DropzoneFileStore(f, this._filters, this._createVideoTusUrl));
    });

    fileRejections.forEach(({ file, errors }) => {
      const error = errors.find(e => e.code === 'file-too-large') ? 'Your file is too large to upload (Maximum size is 60 MB)' : `File "${file.name}" rejected`;
      toastr.error({ message: `${error}. Size: ${toFileSize(file.size)}` });
    });

    if (autoUpload && files.length) {
      this.uploadFiles(true);
    }
  }

  @action reset() {
    if (this._pendingUploads) {
      this._pendingUploads.forEach(u => u.cancel());
      this._pendingUploads = null;
    }
    this.droppedFiles.forEach(f => {
      f.dispose();
    });
    this.droppedFiles.clear();
    this.form.$('category').set('value', '');
  }

  @action uploadFiles(autoUpload) {
    this.isUploading = true;
    Promise.resolve(this._onUploading && this._onUploading(this.droppedFiles))
      .then(() => Promise.all(this.droppedFiles.map(f => f.validate())))
      .then(res => {
        const notValid = res.find(f => !f.isValid);
        if (notValid) { return null; }

        // All files where good, now we can upload them
        const category = this.form.$('category').value;
        if (this._onManualFilesHandle) {
          return Promise.resolve(this._onManualFilesHandle(this.droppedFiles, category, f => this.removeFile(f)))
            .then(() => this.reset())
            .catch(() => {});
        } else {
          const baseUrl = this._getBaseUrl();
          this._pendingUploads = this.droppedFiles.map(f =>
            f.upload(baseUrl, category)
              .then(r => {
                if (r) {
                  this.removeFile(f);
                  return this._onUploaded && this._onUploaded(r, f.file);
                } else if (autoUpload) {
                  const err = new Error(f.errorMessage);
                  err.isCustom = true;
                  log.catchAndNotify(err);
                  this._onError && this._onError(err);
                }
                return null;
              })
          );
          return Promise.all(this._pendingUploads);
        }
      })
      .then(() => {
        if (!this.droppedFiles.length) { return this._onAllUploaded && this._onAllUploaded(); }
        return null;
      })
      .catch(DialogClosedError, () => {}) // Do nothing on cancel dialog, since it was cancelled
      .finally(action(() => {
        this.isUploading = false;
        this._pendingUploads = null;
        if (autoUpload) { this.reset(); }
      }))
      .done();
  }

  @action removeFile(file) {
    if (file) {
      file.dispose();
      this.droppedFiles.remove(file);
    }
  }

  async _loadCategories(t) {
    if (!this._loadCategoriesPromise) {
      const url = getUrlPath(this._getBaseUrl(), 'categories');
      this._loadCategoriesPromise = api.get(url)
        .then(res => res.data)
        .catch(err => {
          this._loadCategoriesPromise = null;
          log.catchAndNotify(err);
          return [];
        });
    }
    const data = await this._loadCategoriesPromise;
    return { options: data.filter(l => !t || l.toLowerCase().indexOf(t.toLowerCase()) !== -1).map(l => ({ name: l, value: l })) };
  }

  _getBaseUrl() {
    return typeof this._baseUrl === 'function' ? this._baseUrl() : this._baseUrl;
  }

  dispose() {
    this.reset();
  }
}

export class DropzoneEditFileStore {
  @observable isSaving = false;

  constructor({ file, baseUrl, onDelete, onUpdate, onClose }) {
    makeObservable(this);
    this._url = `${baseUrl}/${file.id}`;
    this.thumbnail = getUrlPath(this._url, 'thumbnail');
    this._file = file;
    this._onDelete = onDelete;
    this._onUpdate = onUpdate;
    this._onClose = onClose;

    this.ext = getFileExtension(file.name);
    this.form = new Form({
      fields: [
        {
          name: 'name',
          label: 'Name',
          value: getFileName(file.name),
          validators: [ isRequired ]
        },
        {
          name: 'date',
          label: 'Date',
          type: 'date',
          value: file.date ? moment(file.date) : null,
          validators: [ isRequired ]
        },
        {
          name: 'category',
          label: 'Category',
          value: file.category,
          type: 'selectasync',
          extra: {
            allowFreeInput: true,
            initialValue: file.category ? { name: file.category, value: file.category } : null,
            loadOptions: async t => {
              if (!this._loadCategoriesPromise) {
                const url = getUrlPath(baseUrl, 'categories');
                this._loadCategoriesPromise = api.get(url)
                  .then(res => res.data)
                  .catch(err => {
                    this._loadCategoriesPromise = null;
                    log.catchAndNotify(err);
                    return [];
                  });
              }
              const data = await this._loadCategoriesPromise;
              return { options: data.filter(l => !t || l.toLowerCase().indexOf(t.toLowerCase()) !== -1).map(l => ({ name: l, value: l })) };
            }
          }
        },
        {
          name: 'description',
          type: 'textarea',
          value: file.description,
          label: 'Description'
        }
      ]
    });
  }

  @action close() {
    return this._onClose && this._onClose();
  }

  @action save(e) {
    this.isSaving = true;
    this.form.submitPromise(e)
      .then(() => {
        const data = this.form.values();
        data.name += this.ext;
        const fullData = Object.assign({}, this._file, data);
        return api.put(this._url, fullData);
      })
      .then(res => this._onUpdate && this._onUpdate(res.data))
      .then(() => this.close())
      .catch(ValidationError, () => {})
      .catch(log.catchAndNotify)
      .finally(action(() => (this.isSaving = false)))
      .done();
  }

  @action delete() {
    this.isSaving = true;
    const fileName = this.form.$('name').value + this.ext;
    dialog.show({
      title: 'Delete Upload',
      description: `Are you sure you want to delete ${fileName}?`
    })
      .then(() => api.delete(this._url))
      .then(() => this._onDelete && this._onDelete(this._file))
      .then(() => this.close())
      .catch(DialogClosedError, action(() => { this.isSaving = false; }))
      .catch(action(err => {
        this.isSaving = false;
        log.catchAndNotify(err);
      }))
      .done();
  }

  @action download() {
    api.openFile(this._url);
  }

  dispose() { }
}

let currentId = 0;
class DropzoneFileStore {
  @observable isUploading = false;
  @observable errorMessage = null;
  @observable errorLink = null;
  @observable uploadProgress = null;
  @observable duration = null;

  @observable processing = false;

  constructor(file, filters, createVideoTusUrl) {
    makeObservable(this);
    this._filters = filters;
    this._createVideoTusUrl = createVideoTusUrl;
    this.id = currentId++;
    this.isVideo = isVideoType(file.type);
    this.file = file;
    this.preview = window.URL.createObjectURL(file);
    this.ext = this.isVideo ? '.mp4' : getFileExtension(file.name);

    this.form = new Form({
      fields: [
        {
          name: 'name',
          label: 'Name',
          value: getFileName(file.name),
          validators: [ isRequired ]
        },
        {
          name: 'date',
          label: 'Date',
          type: 'date',
          value: moment(),
          validators: [ isRequired ]
        },
        {
          name: 'description',
          type: 'textarea',
          label: 'Description'
        }
      ]
    });
  }

  dispose() {
    window.URL.revokeObjectURL(this.preview);
  }

  @action setDuration(duration) {
    this.duration = duration;
  }

  @flow *validate() {
    this.isUploading = true;
    try {
      return yield this.form.validate({ showErrors: true });
    } finally {
      this.isUploading = false;
    }
  }

  @flow *upload(url, category) {
    this.errorMessage = null;
    this.errorLink = null;
    this.isUploading = true;
    this.processing = false;
    this.uploadProgress = 0;
    let res;
    if (this.isVideo) {
      res = yield this._uploadVideo(url, category);
    } else {
      res = yield this._uploadFile(url, category);
    }
    this.isUploading = false;
    this.processing = false;
    this.uploadProgress = null;
    return res;
  }

  @flow *_uploadFile(url, category) {
    try {
      const data = this.form.values();

      const formData = new FormData();
      formData.append('date', data.date.toISOString());
      if (category) { formData.append('category', category); }
      if (data.description) { formData.append('description', data.description); }
      if (this._filters) { formData.append('filters', JSON.stringify(this._filters)); }
      formData.append('file', this.file, data.name + this.ext);

      const config = {
        headers: { 'content-type': 'multipart/form-data' },
        onUploadProgress: action((e) => (this.uploadProgress = Math.round((e.loaded * 100) / e.total))),
        timeout: 0 // Uploads can take a while! Have no timeout...
      };

      const res = yield api.post(url, formData, config);
      return res.data.r[0];
    } catch (err) {
      this.errorMessage = err.response?.data?.error ? err.response.data.error.message : 'Unknown error occurred';
      this.errorLink = err?.response?.data?.error?.linkName;
      return null;
    }
  }

  @flow *_uploadVideo(url, category) {
    try {
      const data = this.form.values();
      const postData = {
        name: data.name + this.ext,
        date: data.date.toISOString(),
        category,
        description: data.descriptio,
        size: this.file.size
      };

      // Get the TUS upload endpoint
      const postPromise = this._createVideoTusUrl ? this._createVideoTusUrl(postData) : api.post(url, postData).then(r => r.data);
      const [ res, tus ] = yield Promise.all([ postPromise, loadTus() ]);

      // Upload the video file
      const { id, url: tusUrl } = res;
      yield new Promise((resolve, reject) => {
        const tusUpload = new tus.Upload(this.file, {
          chunkSize: CLOUDFLARE_STREAM_CHUNK_SIZE,
          uploadUrl: tusUrl,
          onError: reject,
          onSuccess: resolve,
          onProgress: action((bytesUploaded, bytesTotal) => (this.uploadProgress = Math.round((bytesUploaded * 100) / bytesTotal)))
        });
        tusUpload.start();
        return null;
      });
      postData.id = id;

      // Poll the status until the video is ready
      this.uploadProgress = 0;
      this.processing = true;
      yield new Promise((resolve, reject) => {
        const pollAttempt = async() => {
          try {
            const { status, pct } = (await api.post(getUrlPath(url, `${id}/status`))).data;
            if (status === 'processing' || status === 'uploading') {
              this.uploadProgress = pct || 0;
              setTimeout(() => pollAttempt(), 2000);
            } else {
              resolve();
            }
          } catch (err) {
            reject(err);
          }
        };
        setTimeout(() => pollAttempt(), 2000);
      });

      return postData;
    } catch (err) {
      this.errorMessage = err.response?.data?.error ? err.response.data.error.message : (typeof err === 'string' ? err : 'Unknown error occurred');
      this.errorLink = err?.response?.data?.error?.linkName;
      return null;
    }
  }
}